import { cn } from '@/lib/cn'
import type { ReactNode } from 'react'

/**
 * Page chrome. `PageShell` owns the gutters and vertical rhythm that every
 * screen shares; `PageHeader` owns the sticky title bar, so primary actions stay
 * reachable without scrolling.
 */
export function PageShell({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('mx-auto w-full max-w-6xl px-4 pt-5 pb-24 sm:px-6 sm:pt-6 md:pb-10', className)}>
      {children}
    </div>
  )
}

export function PageHeader({
  title,
  subtitle,
  actions,
  meta,
  className,
}: {
  title: ReactNode
  subtitle?: ReactNode
  /** Primary actions. Always rendered at the right, never wrapped below. */
  actions?: ReactNode
  /** Rendered under the title, above the subtitle. Used for badge rows. */
  meta?: ReactNode
  className?: string
}) {
  return (
    <header
      className={cn(
        'sticky top-0 z-20 -mx-4 mb-5 border-b border-line bg-canvas/85 px-4 backdrop-blur-md sm:-mx-6 sm:px-6',
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 py-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-balance text-ink">
            {title}
          </h1>
          {subtitle ? (
            <p className="mt-0.5 text-sm text-pretty text-ink-faint">{subtitle}</p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      {meta}
    </header>
  )
}
